// src/clinic/pages/patient/components/PatientCellRenderer.tsx
import React from "react";
import {
  Avatar,
  Box,
  Icon,
  InlineStack,
  Tooltip,
  Badge,
} from "@medentic/polaris-react";
import { NoteIcon } from "@medentic/polaris-icons";
import { RenderDate } from "@shared/ui";
import { getTone } from "../utils";
import {
  Patient,
  PatientJsonBadge,
  Column,
  AvatarSource,
  CoreAvatar,
} from "../types";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@features/clinics/utils";
import i18n from "@shared/i18n/i18n";
import { Tag } from "@shared/ui";
import { JSONBadgeRenderer } from "@shared/ui";
import useFormattedCurrency from "@shared/hooks/common/useFormattedCurrency";
import type { Tag as TagEntity } from "@shared/types/tags";

export type TagsById = Map<string, TagEntity>;
interface PatientCellRendererProps {
  column: Column;
  patient: Patient;
  allowBadgeRefresh?: boolean;
  tagsById?: TagsById;
}

const PatientCellRenderer: React.FC<PatientCellRendererProps> = ({
  column,
  patient,
  allowBadgeRefresh = false,
  tagsById,
}) => {
  const { t } = useTranslation();
  const cellValue = patient[column.name] ?? "";
  const { formattedValue } = useFormattedCurrency({
    value: cellValue,
  });
  const renderCellContent = () => {
    if (column?.renderer?.properties?.currency === true) {
      return formattedValue;
    }

    if (column?.id === "date") {
      return (
        <RenderDate
          date={String(cellValue)}
          isAgoFormat={column.renderer?.properties?.format === "ago"}
        />
      );
    }
    if (column?.id === "tags" && Array.isArray(cellValue)) {
      const tagIds: string[] = cellValue;
      return (
        <InlineStack gap="200">
          {tagIds.map((tagId) => {
            const tag = tagsById?.get(tagId);
            return (
              <Tag
                key={tagId}
                tagId={tagId}
                tagLabel={tag?.name ?? tagId}
                colorName={tag?.color ?? "hsl(0, 0%, 80%)"}
              />
            );
          })}
        </InlineStack>
      );
    }
    if (column?.id === "number" && !!cellValue) {
      return formatNumber(Number(cellValue), i18n.language);
    }

    if (column?.id === "jsonBadge") {
      const badgeProps = cellValue as unknown as PatientJsonBadge;
      return (
        <JSONBadgeRenderer
          badge={badgeProps}
          patientId={patient.id}
          allowRefresh={allowBadgeRefresh}
        />
      );
    }

    if (column?.name === "core_avatar") {
      const avatarData = patient["core_avatar"] as CoreAvatar;
      if (
        avatarData &&
        typeof avatarData === "object" &&
        Array.isArray(avatarData.sources)
      ) {
        const srcSet = avatarData.sources
          .map(({ src, width }: AvatarSource) => `${src} ${width}w`)
          .join(", ");
        const sizes = "28px";

        return (
          <img
            src={avatarData.value}
            srcSet={srcSet}
            sizes={sizes}
            alt={patient["core_firstname"]}
            height={28}
            width={28}
            style={{
              height: "28px",
              width: "28px",
              objectFit: "cover",
              borderRadius: "6px",
            }}
            loading="lazy"
          />
        );
      } else {
        const initials = [
          patient["core_firstname"]?.charAt(0),
          patient["core_lastname"]?.charAt(0),
        ]
          .filter(Boolean)
          .join("");
        return <Avatar initials={initials} name={patient["core_firstname"]} />;
      }
    }

    if (column?.id === "bool") {
      return (
        <Badge progress={cellValue ? "complete" : "incomplete"}>
          {cellValue ? t("common.yes") : t("common.no")}
        </Badge>
      );
    }

    if (column?.name === "core_patient_owner" && cellValue) {
      const doctorOption = column.options?.find(
        (option) => option.value === cellValue
      );
      return doctorOption?.label || cellValue;
    }

    return (
      column.options?.find((option) => option.value === cellValue)?.label ??
      String(cellValue)
    );
  };

  return (
    <InlineStack gap={"200"} wrap={false} blockAlign="center">
      {renderCellContent()}
      {column?.name === "core_avatar" && patient["core_private_note"] && (
        <Box>
          <Tooltip content={patient["core_private_note"]}>
            <Icon
              source={NoteIcon}
              tone={getTone(patient["core_private_note_tone"])}
            />
          </Tooltip>
        </Box>
      )}
    </InlineStack>
  );
};

export default PatientCellRenderer;
